import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { Grid, Stack, type Theme, useMediaQuery } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { useFields } from '@pbt/pbt-ui-components'

import { ConversationTransport } from '~/api/graphql/generated/types'
import { ContextualBackButton } from '~/components/common/buttons/ContextualBackButton'
import BusinessShareIcon from '~/components/common/icons/BusinessShareIcon'
import UserSelect, {
  UserSelectFilterScope,
} from '~/components/common/inputs/UserSelect'
import { RightRailOverlayProvider } from '~/components/dashboard/soapV2/context/RightRailOverlayContext'
import FeatureToggle from '~/constants/featureToggle'
import { fetchClient } from '~/store/actions/clients'
import {
  editConversation,
  setAssigneeForConversation,
} from '~/store/actions/conversations'
import { fetchMember } from '~/store/actions/members'
import { getClientIsLoading } from '~/store/reducers/clients'
import { getFeatureToggle } from '~/store/reducers/constants'
import {
  getConversationById,
  getConversationsIsNotAccessibleFromBusinessErrorType,
} from '~/store/reducers/conversations'
import { getUser } from '~/store/reducers/users'
import useIsCurrentContextItem from '~/utils/useIsCurrentContextItem'

import EditableTitle from '../common/EditableTitle'
import TransportIcon from '../TransportIcon'
import Chat from './Chat'
import MessageFormattingArea from './MessageFormattingArea'
import ChatRightRail from './right-rail/ChatRightRail'
import { CommunicationsPatientMedicalRecordRightRail } from './right-rail/CommunicationsPatientMedicalRecordRightRail'
import SystemMessageAlert from './SystemMessageAlert'
import SystemUserSelect from './SystemUserSelect'

const useStyles = makeStyles(
  (theme) => ({
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px - ${theme.constants.progressBarHeight}px)`,
      width: '100%',
      overflow: 'hidden',
      [theme.breakpoints.up('lg')]: {
        gridTemplateColumns: '7fr 5fr',
      },
    },
    mainContentGrid: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.colors.brandPageBg,
      overflow: 'hidden',
      minWidth: 0,
    },
    rightRailGrid: {
      display: 'none',
      overflow: 'hidden',
      [theme.breakpoints.up('lg')]: {
        display: 'flex',
        flexDirection: 'column',
      },
    },
    root: {
      backgroundColor: theme.colors.brandPageBg,
      height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px - ${theme.constants.progressBarHeight}px)`,
      width: '100%',
      [theme.breakpoints.up('lg')]: {
        width: `calc(100% - ${theme.constants.rightRailCollapsedWidth}px)`,
      },
    },
    rootWide: {
      width: '100%',
    },
    chat: {
      flex: 1,
      overflow: 'auto',
    },
    backPanel: {
      backgroundColor: theme.colors.grayGray4,
      paddingLeft: theme.spacing(3),
    },
    chatHeader: {
      height: 73,
      padding: theme.spacing(1, 3),
      borderRadius: 2,
      borderBottom: theme.constants.tableBorder,
      backgroundColor: theme.colors.grayWhite,
      boxShadow: theme.constants.listItemShadow,
    },
    transportIcon: {
      color: theme.colors.grayGray3,
      fontSize: '3.6rem',
    },
  }),
  { name: 'ChatContainer' },
)

const ChatContainer = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { conversationId } = useParams() as { conversationId: string }
  const isMedium = useMediaQuery<Theme>((theme) => theme.breakpoints.down('lg'))
  const [rightRailVisibilityStatus, setRightRailVisibilityStatus] =
    useState(!isMedium)
  const { t } = useTranslation(['Common', 'Communications'])

  const conversation = useSelector(getConversationById(conversationId))

  const { title, transport, client: conversationClientId } = conversation || {}

  const assignee = useSelector(getUser(conversation?.assigneeId))
  const conversationClient = useSelector(getUser(conversationClientId))
  const clientIsLoading = useSelector(getClientIsLoading)
  const isConversationNotAccessibleFromBusinessErrorType = useSelector(
    getConversationsIsNotAccessibleFromBusinessErrorType,
  )
  const isCvcSystemMessageSuppressionEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_SYSTEM_MESSAGE_SUPPRESSION),
  )
  const isCommunicationsRightRailEnabled = useSelector(
    getFeatureToggle(FeatureToggle.ENABLE_COMMUNICATIONS_RIGHT_RAIL),
  )

  const isSystemMessage =
    isCvcSystemMessageSuppressionEnabled &&
    Boolean(conversation?.isSystemMessage)

  const isContextItem = useIsCurrentContextItem(conversation)

  useEffect(() => {
    setRightRailVisibilityStatus(!isMedium)
  }, [isMedium])

  const {
    fields: { assigneeId },
    reset,
  } = useFields(
    [
      {
        name: 'assigneeId',
        label: t('Common:ASSIGN_TEAM_MEMBER'),
        initialValue: conversation?.assigneeId,
      },
    ],
    false,
  )

  const setTitle = (newTitle: string) => {
    dispatch(editConversation(conversationId, { title: newTitle }))
  }

  useEffect(() => {
    if (conversation?.assigneeId && !assignee) {
      dispatch(fetchMember(conversation?.assigneeId, true))
    }
  }, [conversation?.assigneeId])

  useEffect(() => {
    reset()
  }, [conversation])

  const isFullConversationClient = Boolean(conversationClient?.patients)

  useEffect(() => {
    if (conversationClientId && !clientIsLoading && !isFullConversationClient) {
      dispatch(fetchClient({ clientId: conversationClientId }))
    }
  }, [conversationClientId, clientIsLoading])

  useEffect(() => {
    if (isConversationNotAccessibleFromBusinessErrorType) {
      navigate('/communications')
    }
  }, [isConversationNotAccessibleFromBusinessErrorType])

  const withRightRail = rightRailVisibilityStatus || isMedium
  const isConversationLoaded = Boolean(transport)
  const hasPatient =
    isConversationLoaded &&
    Boolean(conversationClientId && conversation?.patient)
  const showNewRightRail = isCommunicationsRightRailEnabled && hasPatient

  const chatHeader = (
    <Grid container item alignItems="center" className={classes.chatHeader}>
      <Grid item>
        <TransportIcon
          classes={{ transportIcon: classes.transportIcon }}
          type={transport}
        />
      </Grid>

      <EditableTitle
        endAdornment={
          <BusinessShareIcon
            businessIds={
              conversation?.businessId ? [conversation.businessId] : []
            }
          />
        }
        readOnly={!isContextItem}
        setTitle={setTitle}
        title={title}
      />

      <Grid item data-testid="user-select">
        {isSystemMessage ? (
          <SystemUserSelect />
        ) : (
          <UserSelect
            disabled={isSystemMessage}
            field={{
              ...assigneeId,
              setValue: (value) => {
                assigneeId.setValue(value)
                dispatch(setAssigneeForConversation(conversationId, value))
              },
            }}
            filterScope={UserSelectFilterScope.Staff}
            label={assigneeId.label}
            readOnly={!isContextItem}
          />
        )}
      </Grid>
    </Grid>
  )

  const messageInput = isContextItem && (
    <Grid container item>
      {isSystemMessage && transport === ConversationTransport.Email ? (
        <SystemMessageAlert
          clientId={conversation?.client}
          patientId={conversation?.patient}
        />
      ) : (
        <MessageFormattingArea conversationId={conversationId} />
      )}
    </Grid>
  )

  if (showNewRightRail) {
    return (
      <div className={classes.gridContainer}>
        <div className={classes.mainContentGrid}>
          <ContextualBackButton
            buttonText={t('Common:PRACTICE_INBOX')}
            navigateBackToContext={() => navigate('/communications')}
          />
          {chatHeader}
          <Stack className={classes.chat}>
            <Chat conversationId={conversationId} />
          </Stack>
          {messageInput}
        </div>
        <div className={classes.rightRailGrid}>
          <RightRailOverlayProvider>
            <CommunicationsPatientMedicalRecordRightRail
              conversationId={conversationId}
            />
          </RightRailOverlayProvider>
        </div>
      </div>
    )
  }

  const showLegacyRightRail = isConversationLoaded && rightRailVisibilityStatus

  return (
    <Grid
      container
      className={classNames({
        [classes.root]: withRightRail,
        [classes.rootWide]: !withRightRail,
      })}
      direction="column"
      wrap="nowrap"
    >
      {isConversationLoaded && (
        <ChatRightRail
          conversationId={conversationId}
          visible={showLegacyRightRail}
          onVisibilityChange={setRightRailVisibilityStatus}
        />
      )}

      <ContextualBackButton
        buttonText={t('Common:PRACTICE_INBOX')}
        navigateBackToContext={() => navigate('/communications')}
      />

      {chatHeader}

      <Stack className={classes.chat}>
        <Chat conversationId={conversationId} />
      </Stack>

      {messageInput}
    </Grid>
  )
}

export default ChatContainer
